package com.corbettcode.allaboutrocks.pages.typesof

import androidx.compose.runtime.Composable
import com.corbettcode.allaboutrocks.HeadlineTextStyle
import com.corbettcode.allaboutrocks.HeroContainerStyle
import com.corbettcode.allaboutrocks.ImageCreditStyle
import com.corbettcode.allaboutrocks.PageHeadlineStyle
import com.corbettcode.allaboutrocks.PageListStyle
import com.corbettcode.allaboutrocks.PageSectionTitleStyle
import com.corbettcode.allaboutrocks.SectionTextStyle
import com.corbettcode.allaboutrocks.components.layouts.PageLayoutData
import com.corbettcode.allaboutrocks.components.widgets.CarouselOfSections
import com.corbettcode.allaboutrocks.pages.LearnBookRatingStyle
import com.corbettcode.allaboutrocks.pages.LearnBookTitleStyle
import com.corbettcode.allaboutrocks.utility.textColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import kotlin.time.Duration.Companion.seconds

// --- Constants ---
private val HERO_IMAGE_MAX_WIDTH = 600.px
private val FIGURE_IMAGE_MAX_WIDTH = 600.px
private val BOOK_COVER_MAX_WIDTH = 400.px

@InitRoute
fun initTypesOfAgate(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Types of Rocks"))
}

/** How a gas bubble in lava ends up as a banded stone. */
private val agateFormation = listOf(
    "A cavity to fill" to "Gas bubbles trapped in cooling lava leave rounded voids, and cracks and fossil hollows do the same job. Anywhere a space stays open underground, agate has somewhere to grow."
    , "Silica-bearing water" to "Groundwater working through weathered volcanic rock carries dissolved silica, seeps into the cavity, and starts depositing chalcedony on the walls."
    , "Layer after layer, inward" to "Each pulse of silica lays down a new skin on top of the last, so the bands run parallel to the cavity wall and the stone grows from the outside in — the reverse of how most crystals build."
    , "Impurities draw the lines" to "Iron, manganese, and other trace elements vary from pulse to pulse, which is what makes one band red, the next white, the next grey."
    , "Quartz finishes the middle" to "If the silica runs out before the cavity fills, the last of it crystallizes slowly into visible quartz points, leaving a hollow, crystal-lined center."
)

/** What to check when you think you have one. */
private val agateKeyCharacteristics = listOf(
    "Banding" to "The defining feature. Concentric bands follow the shape of the cavity the agate grew in, and in fortification agate they turn sharp corners like the walls of a star fort seen from above."
    , "Translucency" to "Light passes through, at least at the edges. This is what separates agate from jasper, which is opaque no matter how thin you slice it."
    , "Hardness" to "6.5–7 on the Mohs scale. It scratches glass, shrugs off a steel knife, and takes a mirror polish."
    , "Fracture" to "Conchoidal — smooth curved breaks with edges sharp enough to cut. There is no cleavage, so it breaks in any direction equally."
    , "Waxy rind" to "In the field you rarely see the bands. What you see is a lumpy nodule with a dull, waxy, often pitted skin, heavier than the basalt around it."
    , "Luster" to "Waxy to greasy on a fresh break, glassy once polished."
)

/** The named agates, by pattern or by where they come from. */
private val agateVarieties = listOf(
    "Fortification agate" to "The classic. Concentric bands with sharp angular turns that look like the plan of a walled fort. Most banded agate is some version of this."
    , "Moss and dendritic agate" to "Not banded at all — translucent chalcedony with dark mineral inclusions branching through it like plants. The \"moss\" is manganese or iron oxide, not anything that was ever alive."
    , "Plume agate" to "Inclusions that spread in soft feathery plumes rather than branching dendrites. Oregon produces some of the best."
    , "Thundereggs" to "Knobby spheres weathered out of rhyolite, plain on the outside and filled with agate or opal inside. They are Oregon's state rock, and cutting one open is the whole point."
    , "Lake Superior agate" to "Small, tough, deeply red-and-white banded pebbles scattered by glaciers across the upper Midwest. Minnesota's state gemstone."
    , "Montana moss agate" to "Clear to milky agate carrying black dendrites, collected from the gravel bars of the Yellowstone River."
    , "Crazy lace and Laguna agate" to "Mexican material — crazy lace tangles its bands into tight chaotic folds, while Laguna is prized for tight, evenly colored fortification banding."
    , "Iris agate" to "Cut thin enough and held to a light, the fine banding diffracts the light into spectral colors."
    , "Fire agate" to "Iridescent brown agate from the Southwest, where thin films of iron oxide between silica layers throw back reds and greens."
    , "Enhydro agate" to "A nodule that never fully dried out, with water still trapped in a cavity inside — you can see the bubble move when you tilt it."
)

/** Outside references, paired with what each one is actually good for. */
private val agateFurtherReading = listOf(
    Triple(
        "Wikipedia: Agate"
        , "The general overview — how the banding forms, the named varieties, and where the word comes from, with sources worth following."
        , "https://en.wikipedia.org/wiki/Agate"
    )
    , Triple(
        "Mindat: Agate"
        , "Mineral data and a locality database running to a couple of thousand entries — the place to look up whether agate has been recorded somewhere you are headed."
        , "https://www.mindat.org/min-51.html"
    )
    , Triple(
        "Geology.com: Agate"
        , "Photograph-heavy coverage of agate as a gem material, including a good survey of the named varieties."
        , "https://geology.com/gemstones/agate/"
    )
    , Triple(
        "Minerals.net: Agate"
        , "A variety-by-variety breakdown, handy when you are trying to put a name to a particular pattern."
        , "https://www.minerals.net/mineral/agate.aspx"
    )
    , Triple(
        "Wikipedia: Thunderegg"
        , "A closer look at the knobby rhyolite nodules of central Oregon, which are the state rock and worth a trip of their own."
        , "https://en.wikipedia.org/wiki/Thunderegg"
    )
)

@Composable
private fun AgateFigure(src: String, alt: String, caption: String, credit: String) {
    Column(
        Modifier
            .fillMaxWidth()
            .gap(0.5.cssRem)
        , horizontalAlignment = CenterHorizontally
    ) {
        Image(
            src = src
            , alt = alt
            , modifier = Modifier
                .maxWidth(FIGURE_IMAGE_MAX_WIDTH)
                .fillMaxWidth()  // shrinks naturally on small screens
        )
        P { Em { Text(caption) } }
        SpanText(credit, ImageCreditStyle.toModifier())
    }
}

@Composable
private fun AgateSectionTitle(text: String) {
    SpanText(
        text
        , PageSectionTitleStyle.toModifier()
            .color(textColor())
    )
}

@Composable
private fun TypesOfAgateHeadline() {
    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Agate"
                , PageHeadlineStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
            )
        }
    }
}

@Composable
private fun TypesOfAgateHero() {

    TypesOfAgateHeadline()

    // 1 column on mobile, 2 columns on md+ (image left, text right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2)
        , modifier = HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {
                Image(
                    src = "/images/typesof/agate/crazy_lace_agate.jpg"
                    , alt = "Polished crazy lace agate slice with tightly folded bands"
                    , modifier = Modifier
                        .maxWidth(HERO_IMAGE_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
                P { Em { Text("Crazy lace agate from Mexico — banding folded and crumpled by the shape of the cavity it grew in.") } }
                SpanText(
                    "Photo: James St. John, CC BY 2.0, via Wikimedia Commons"
                    , ImageCreditStyle.toModifier()
                )
            }
        }

        Column(Modifier.gap(2.cssRem)) {
            P {
                Text("Agate is banded chalcedony — the same cryptocrystalline quartz as carnelian and jasper, but laid down in visible layers. It takes its name from the Achates river in Sicily, where classical writers said the first pieces were found, and it has been cut and polished for as long as people have been cutting stone. For most rockhounds it is the stone: common enough to find, tough enough to tumble, and different in every single piece.")
            }

            AgateSectionTitle("How It Forms")
            P {
                Text("An agate is a cast of a hole. Everything about it — the shape, the banding, the crystal pocket in the middle — comes from silica slowly filling a space in a rock:")
            }
        }
    }

    Column(
        HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Ul(PageListStyle.toAttrs()) {
            agateFormation.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }

        P {
            Text("That is the outline everyone agrees on. Exactly why the bands are so regular, and why the colors alternate as rhythmically as they do, is still argued over — agate banding is one of those everyday things that turns out to be genuinely unsolved.")
        }

        AgateFigure(
            src = "/images/typesof/agate/agate_geode_quartz_center.jpg"
            , alt = "Cut agate geode with banded rim and a quartz crystal center"
            , caption = "A cut agate geode — banded chalcedony around the rim, grading inward to quartz crystals where the silica ran out before the cavity filled."
            , credit = "Photo: Plutos, public domain, via Wikimedia Commons"
        )
    }

    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )

    Column(
        Modifier
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        AgateSectionTitle("Key Characteristics")

        Ul(PageListStyle.toAttrs()) {
            agateKeyCharacteristics.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(": $description")
                }
            }
        }

        AgateFigure(
            src = "/images/typesof/agate/agate_nodule_succor_creek.jpg"
            , alt = "Cut agate nodule from Succor Creek, Oregon, with red body and pale center"
            , caption = "A cut agate nodule from the Succor Creek area, Harney County, Oregon — the rough grey rind on the outside is all you would have seen in the field."
            , credit = "Photo: James St. John, CC BY 2.0, via Wikimedia Commons"
        )

        AgateSectionTitle("Types and Varieties")
        P {
            Text("Agates get named for their pattern, for where they come from, or both. A few of the ones you will hear about:")
        }

        Ul(PageListStyle.toAttrs()) {
            agateVarieties.forEach { (name, description) ->
                Li {
                    B { Text(name) }
                    Text(" — $description")
                }
            }
        }

        AgateFigure(
            src = "/images/typesof/agate/moss_agate.jpg"
            , alt = "Slab of moss agate with black dendritic inclusions"
            , caption = "Moss agate — manganese oxide branching through translucent chalcedony. Nothing organic was ever involved, despite the name."
            , credit = "Photo: Shannon Heinle, CC0, via Wikimedia Commons"
        )

        AgateFigure(
            src = "/images/typesof/agate/thundereggs_oregon.jpg"
            , alt = "Two cut thundereggs showing agate centers in a red rind"
            , caption = "Thundereggs from Richardson Ranch near Madras, Oregon, cut and polished — knobby and unremarkable until you saw one in half."
            , credit = "Photo: Lech Darski, CC BY-SA 4.0, via Wikimedia Commons"
        )

        AgateSectionTitle("Agate, Jasper, and Carnelian")
        P {
            Text("All three are the same mineral in the same family, and the names describe appearance rather than chemistry. Agate is translucent and banded. Jasper is opaque, carrying enough iron and clay to stop light, and its patterns swirl or blotch rather than forming parallel layers. Carnelian is translucent like agate but colored an even red-orange with no banding to speak of. Plenty of stones sit between the categories — banded carnelian, jasper with agate seams running through it — and there is no authority to settle what those should be called.")
        }

        AgateSectionTitle("Where to Find It")
        P {
            Text("Agate turns up anywhere old lava has been weathering long enough, which makes the Pacific Northwest particularly good ground. Oregon alone has the beaches around Newport, the thunderegg beds of central Oregon, and the Succor Creek and Owyhee country in the southeast; Washington has the Columbia basin gravels. Farther afield, the Yellowstone River gravels in Montana produce moss agate, glacial drift across Minnesota and Wisconsin carries Lake Superior agates, and Brazil, Uruguay, and Mexico supply most of the agate sold commercially.")
        }

        AgateSectionTitle("Finding Them in the Field")
        P {
            Text("Agate hunting rewards light and patience more than effort. Go when the sun is low and keep it in front of you, so the stones light up from the far side instead of going flat — a translucent pebble glows and everything around it stays dull. Wet gravel reads far better than dry, so beaches after a wave and creek bars after rain are worth more than a dry slope. Learn the waxy, greasy-looking rind rather than looking for bands, because the bands are hidden until you cut. And check the rules before you fill a bucket: beaches, BLM ground, private land, and state parks each have their own limits.")
        }

        AgateSectionTitle("Working With It")
        P {
            Text("Agate is the material most people learn on. It is hard, it has no cleavage to catch you out, and it polishes beautifully, which makes it forgiving in a tumbler and predictable on a saw. Slab a nodule across its widest axis to open up the banding, or cut cabochons from the cleaner areas. The one thing to watch for is fractures — a nodule that has been knocked around in a riverbed for a few thousand years often has hairline cracks that will open up under heat or pressure.")
        }

        AgateSectionTitle("For More Information")

        Ul(PageListStyle.toAttrs()) {
            agateFurtherReading.forEach { (title, description, url) ->
                Li {
                    B { Link(url, title) }
                    Text(" — $description")
                }
            }
        }
    }
}

@Composable
private fun TypesOfAgate_BooksSection() {
    Div(SectionTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Books on Agate"
                , PageSectionTitleStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
                    .margin(topBottom = 2.cssRem)
            )
        }

        CarouselOfSections(
            items = booksOnAgateCarouselSlides
            , frameDuration = 8.seconds
        )
    }
}

@Page("/types_of_rocks/agate")
@Layout(".components.layouts.PageLayout")
@Composable
fun TypesOfAgatePage() {
    TypesOfAgateHero()
    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )
    TypesOfAgate_BooksSection()
}

/**
 *
 */
val booksOnAgateCarouselSlides = listOf<@Composable () -> Unit>(
    {
        SimpleGrid(
            numColumns = numColumns(2)
            , modifier = Modifier.fillMaxWidth()
        ) {
            Image(
                src = "/images/learn/books/agatesjasper.webp"
                , alt = "Collecting Agates and Jaspers of North America"
                , modifier = Modifier
                    .maxWidth(BOOK_COVER_MAX_WIDTH)
                    .fillMaxWidth()  // shrinks naturally on small screens
            )

            Column {
                SpanText(
                    "Collecting Agates and Jaspers of North America"
                    , LearnBookTitleStyle.toModifier()
                        .color(textColor())
                        .align(alignment = Alignment.Start)
                )
                SpanText(
                    "4.3 on Goodreads"
                    , LearnBookRatingStyle.toModifier()
                        .color(textColor())
                        .align(alignment = Alignment.Start)
                )

                P {
                    Text("If you have any interest whatsoever in agates and jasper and if you intend to do some traveling, then this is a good book to have. The author does an impressive job showcasing the material that can be found in most of the US states, as well as parts of Canada and Mexico. I like that the author didn’t use museum grade specimens for her examples, which seems rare in the world of rock books. Instead, she uses good, but still obtainable, rock samples. My only real criticism of the book is that the author lists a price/value of the specimens which was likely inaccurate, even at the time of printing, since the market value of rocks can change rapidly.")
                }
            }
        }
    }
    , {
        SimpleGrid(
            numColumns = numColumns(2)
            , modifier = Modifier.fillMaxWidth()
        ) {
            Image(
                src = "/images/learn/books/agate1.webp"
                , alt = "Agates: Treasures of the Earth"
                , modifier = Modifier
                    .maxWidth(BOOK_COVER_MAX_WIDTH)
                    .fillMaxWidth()  // shrinks naturally on small screens
            )

            Column {
                SpanText(
                    "Agates: Treasures of the Earth"
                    , LearnBookTitleStyle.toModifier()
                        .color(textColor())
                        .align(alignment = Alignment.Start)
                )
                SpanText(
                    "4.0 on Goodreads"
                    , LearnBookRatingStyle.toModifier()
                        .color(textColor())
                        .align(alignment = Alignment.Start)
                )

                P {
                    Text("This book provides a very well-rounded overview of the subject of Agates in a way that I haven’t seen done anywhere else. From the formation of agates to how they have been used throughout history, the book covers it all and would make an excellent addition to someone’s library or as a gift for someone who loves agates. It walks to the line of being technical but doesn’t cross it, which makes the material very digestible for the amateur collector.")
                }
            }
        }
    }
)
